'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { isDev } from '../../lib/is-dev';
import {
  getModalEntries,
  getServerModalEntries,
  isModalOutletMounted,
  markModalOutletRendered,
  registerModalOutlet,
  subscribeModalEntries,
  type SModalEntry,
} from '../../lib/modal-outlet';
import { InsideModalProvider, PortalContainerProvider } from '../../lib/portal-container';

/** @internal 엔트리 하나를 자기 host 로 portal 하고, 사라질 때 host 도 정리한다 */
function SModalEntryPortal({ entry }: { entry: SModalEntry }) {
  useEffect(() => {
    // StrictMode 의 mount→unmount→mount 로 cleanup 이 먼저 돌아 host 가 떨어져 나갈 수 있다
    if (!entry.host.isConnected) document.body.appendChild(entry.host);
    return () => entry.host.remove();
  }, [entry]);

  return createPortal(entry.node, entry.host);
}

/**
 * SModalOutlet — 명령형 모달(`SModal.confirm` / `loading` / `create`)이 그려지는 자리.
 *
 * **앱 부트스트랩에서 Provider 안쪽에 한 번만 렌더한다.** 이 outlet 이 있으면 명령형 모달이
 * 앱 렌더 트리의 자식으로 그려져 QueryClient·Router·Theme 등 앱의 Context 를 그대로 상속한다.
 * DOM 상으로는 예전과 같이 `body` 직속으로 portal 되므로 위치·쌓임 순서는 달라지지 않는다.
 *
 * outlet 을 렌더하지 않아도 모달은 뜨지만, 예전처럼 별도 React 루트로 마운트되어 앱의
 * Context 가 닿지 않는다(개발 모드에서 1회 경고).
 *
 * @example
 * // 앱 부트스트랩 — 한 번만. Provider 안쪽이어야 모달이 상속받는다.
 * <QueryClientProvider client={queryClient}>
 *   <RouterProvider router={router} />
 *   <SModalOutlet />
 * </QueryClientProvider>
 */
export function SModalOutlet() {
  // 존재 표시는 렌더 단계에서 한다 — effect 는 자식이 먼저 돌아서, 페이지가 최초 마운트
  // effect 에서 연 모달이 outlet 을 못 보고 폴백해 버린다 (markModalOutletRendered 주석 참고)
  markModalOutletRendered();

  const entries = useSyncExternalStore(
    subscribeModalEntries,
    getModalEntries,
    getServerModalEntries,
  );

  useEffect(() => {
    // outlet 이 둘이면 같은 엔트리를 둘 다 그려 모달이 겹쳐 뜬다 — 앱 전체에 하나만 둔다
    if (isDev() && isModalOutletMounted()) {
      console.warn(
        '[SModalOutlet] 이미 렌더된 SModalOutlet 이 있습니다. 앱 전체에 하나만 두세요 — ' +
          '둘 이상이면 같은 모달이 중복으로 그려집니다.',
      );
    }
    return registerModalOutlet();
  }, []);

  // 명령형 모달은 항상 최상위 모달이다. outlet 이 어쩌다 다른 모달 안에 놓이더라도
  // 그 모달의 portal 컨테이너를 물려받지 않도록 기본값으로 되돌린다.
  return (
    <PortalContainerProvider value={null}>
      <InsideModalProvider value={false}>
        {entries.map(entry => (
          <SModalEntryPortal key={entry.id} entry={entry} />
        ))}
      </InsideModalProvider>
    </PortalContainerProvider>
  );
}
